import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Loader2, Mail, Video, Bug, Copy, Check, AlertCircle, Link as LinkIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { apiClient } from '../../lib/api-client';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';

// ---------------------------------------------------------------------------
// Types mirroring backend
// ---------------------------------------------------------------------------

interface FilterRule {
  field: string;
  operator: string;
  value: string | string[];
  caseSensitive?: boolean;
}

interface EmailInboxConfig {
  mailbox: string;
  type: 'shared' | 'user';
  folder?: string;
  label?: string;
  filters?: FilterRule[];
}

interface MeetingRecordingConfig {
  enabled: boolean;
  level: 'tenant' | 'user';
  userId?: string;
  transcriptFormat?: string;
  filters?: FilterRule[];
}

type JiraEventType =
  | 'jira:issue_created'
  | 'jira:issue_updated'
  | 'comment_created'
  | 'comment_updated'
  | 'issuelink_created'
  | 'attachment_created'
  | 'sprint_started'
  | 'sprint_closed'
  | 'board_updated'
  | 'worklog_created';

interface JiraListenerConfig {
  baseUrl: string;
  projectKey: string;
  label?: string;
  events: JiraEventType[];
  filters?: FilterRule[];
  customerOnly?: boolean;
  agentMention?: string;
  webhookId?: number;
  webhookSyncError?: string;
  webhookUrl?: string;
}

const ALL_JIRA_EVENTS: { value: JiraEventType; label: string }[] = [
  { value: 'jira:issue_created', label: 'Issue Created' },
  { value: 'jira:issue_updated', label: 'Issue Updated' },
  { value: 'comment_created', label: 'Comment Created' },
  { value: 'comment_updated', label: 'Comment Updated' },
  { value: 'issuelink_created', label: 'Issue Link Created' },
  { value: 'attachment_created', label: 'Attachment Created' },
  { value: 'sprint_started', label: 'Sprint Started' },
  { value: 'sprint_closed', label: 'Sprint Closed' },
  { value: 'board_updated', label: 'Board Updated' },
  { value: 'worklog_created', label: 'Worklog Created' },
];

// ---------------------------------------------------------------------------
// Reusable filter row editor
// ---------------------------------------------------------------------------

const GENERIC_FIELD_OPTIONS = ['subject', 'body', 'sender', 'recipients', 'organizer', 'attendees', 'tags'];
const JIRA_FIELD_OPTIONS = ['summary', 'issueType', 'priority', 'status', 'labels', 'component', 'assignee', 'reporter'];
const OPERATOR_OPTIONS = ['contains', 'equals', 'startsWith', 'regex', 'any_of'];

function FilterRow({
  rule,
  onChange,
  onRemove,
  fieldOptions,
}: {
  rule: FilterRule;
  onChange: (updated: FilterRule) => void;
  onRemove: () => void;
  fieldOptions: string[];
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <select
        value={rule.field}
        onChange={(e) => onChange({ ...rule, field: e.target.value })}
        className="px-2 py-1.5 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-xs"
      >
        {fieldOptions.map((f) => (
          <option key={f} value={f}>{f}</option>
        ))}
      </select>
      <select
        value={rule.operator}
        onChange={(e) => onChange({ ...rule, operator: e.target.value })}
        className="px-2 py-1.5 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-xs"
      >
        {OPERATOR_OPTIONS.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
      <Input
        value={Array.isArray(rule.value) ? rule.value.join(', ') : rule.value}
        onChange={(e) => {
          const raw = e.target.value;
          const val = rule.operator === 'any_of' ? raw.split(',').map((s) => s.trim()) : raw;
          onChange({ ...rule, value: val });
        }}
        placeholder={rule.operator === 'any_of' ? 'value1, value2' : 'value'}
        className="flex-1 !py-1.5 text-xs"
      />
      <button type="button" onClick={onRemove} className="text-red-500 hover:text-red-700 shrink-0">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function FilterEditor({
  filters,
  onChange,
  fieldOptions = GENERIC_FIELD_OPTIONS,
  defaultField,
}: {
  filters: FilterRule[];
  onChange: (updated: FilterRule[]) => void;
  fieldOptions?: string[];
  defaultField?: string;
}) {
  const addFilter = () => {
    onChange([...filters, { field: defaultField || fieldOptions[0] || 'subject', operator: 'contains', value: '' }]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Filters (AND logic)</span>
        <button
          type="button"
          onClick={addFilter}
          className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
        >
          <Plus className="w-3 h-3" /> Add filter
        </button>
      </div>
      {filters.map((rule, i) => (
        <FilterRow
          key={i}
          rule={rule}
          fieldOptions={fieldOptions}
          onChange={(updated) => {
            const copy = [...filters];
            copy[i] = updated;
            onChange(copy);
          }}
          onRemove={() => onChange(filters.filter((_, j) => j !== i))}
        />
      ))}
      {filters.length === 0 && (
        <p className="text-xs text-gray-400 italic">No filters — all events will be matched.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Jira webhook registration status (URL + copy + last sync error)
// ---------------------------------------------------------------------------

function JiraWebhookStatus({ listener }: { listener: JiraListenerConfig }) {
  const [copied, setCopied] = useState(false);
  const url = listener.webhookUrl;

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be unavailable */
    }
  };

  const ready = Boolean(listener.baseUrl && listener.projectKey && listener.events.length > 0);
  if (!ready) return null;

  return (
    <div className="space-y-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-2.5">
      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
        <LinkIcon className="w-3.5 h-3.5 text-gray-400" />
        Portal webhook URL
      </div>
      {url ? (
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded break-all">
            {url}
          </code>
          <button
            type="button"
            onClick={() => void copy()}
            className="shrink-0 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title="Copy webhook URL"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      ) : (
        <p className="text-xs text-gray-400 italic">
          Set <code>PUBLIC_URL</code> on the backend to generate a copyable webhook URL.
        </p>
      )}
      <div className="flex items-center gap-2 text-xs">
        {listener.webhookSyncError ? (
          <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
            <AlertCircle className="w-3.5 h-3.5" />
            Registration failed: {listener.webhookSyncError}
          </span>
        ) : listener.webhookId ? (
          <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
            <Check className="w-3.5 h-3.5" />
            Registered with Jira (webhook #{listener.webhookId})
          </span>
        ) : (
          <span className="text-gray-400">
            Not registered with Jira. Add Jira credentials on the stream to auto-register, or paste this URL into Jira manually.
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

interface ListenersDialogProps {
  open: boolean;
  streamId: string;
  streamDisplayName: string;
  /** When set, read/write listeners on this role only (flat model). */
  roleCode?: string;
  roleLabel?: string;
  onClose: () => void;
  onSaved: () => void;
}

export function ListenersDialog({
  open,
  streamId,
  streamDisplayName,
  roleCode,
  roleLabel,
  onClose,
  onSaved,
}: ListenersDialogProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [inboxes, setInboxes] = useState<EmailInboxConfig[]>([]);
  const [recording, setRecording] = useState<MeetingRecordingConfig | null>(null);
  const [jiraListeners, setJiraListeners] = useState<JiraListenerConfig[]>([]);

  const listenersPath = useMemo(
    () =>
      roleCode
        ? `/api/admin/ai-config/streams/${encodeURIComponent(streamId)}/roles/${encodeURIComponent(roleCode)}/listeners`
        : `/api/admin/ai-config/streams/${encodeURIComponent(streamId)}/listeners`,
    [streamId, roleCode],
  );

  useEffect(() => {
    if (!open || !streamId || (roleCode !== undefined && !roleCode)) return;
    setLoading(true);
    setError(null);
    apiClient
      .get<{
        emailInboxes: EmailInboxConfig[];
        meetingRecordings: MeetingRecordingConfig | null;
        jiraListeners: JiraListenerConfig[];
      }>(listenersPath)
      .then((data) => {
        setInboxes(data.emailInboxes || []);
        setRecording(data.meetingRecordings || null);
        setJiraListeners(data.jiraListeners || []);
      })
      .catch((err: any) => setError(err.message))
      .finally(() => setLoading(false));
  }, [open, streamId, roleCode, listenersPath]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = await apiClient.put<{
        emailInboxes?: EmailInboxConfig[];
        meetingRecordings?: MeetingRecordingConfig | null;
        jiraListeners?: JiraListenerConfig[];
      }>(listenersPath, {
        emailInboxes: inboxes,
        meetingRecordings: recording,
        jiraListeners,
      });
      // Reflect server-side webhook registration results (webhookId / sync error / URL).
      if (data?.jiraListeners) {
        setJiraListeners(data.jiraListeners);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Inbox CRUD helpers
  const addInbox = () => {
    setInboxes([...inboxes, { mailbox: '', type: 'shared', label: '', folder: 'Inbox', filters: [] }]);
  };

  const updateInbox = (index: number, patch: Partial<EmailInboxConfig>) => {
    setInboxes(inboxes.map((inbox, i) => (i === index ? { ...inbox, ...patch } : inbox)));
  };

  const removeInbox = (index: number) => {
    setInboxes(inboxes.filter((_, i) => i !== index));
  };

  // Recording helpers
  const enableRecording = () => {
    setRecording({ enabled: true, level: 'tenant', filters: [] });
  };

  const disableRecording = () => {
    setRecording(null);
  };

  // Jira CRUD helpers
  const addJiraListener = () => {
    setJiraListeners([
      ...jiraListeners,
      { baseUrl: '', projectKey: '', label: '', events: ['jira:issue_created'], filters: [], customerOnly: false },
    ]);
  };

  const updateJiraListener = (index: number, patch: Partial<JiraListenerConfig>) => {
    setJiraListeners(jiraListeners.map((jl, i) => (i === index ? { ...jl, ...patch } : jl)));
  };

  const removeJiraListener = (index: number) => {
    setJiraListeners(jiraListeners.filter((_, i) => i !== index));
  };

  const toggleJiraEvent = (listenerIdx: number, event: JiraEventType) => {
    const current = jiraListeners[listenerIdx].events;
    const updated = current.includes(event) ? current.filter((e) => e !== event) : [...current, event];
    updateJiraListener(listenerIdx, { events: updated });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent maxWidth="3xl" className="max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Configure listeners
            {roleCode ? (
              <span className="block text-sm font-normal text-gray-500 dark:text-gray-400 mt-1">
                Role: {roleLabel || roleCode} · Stream: {streamDisplayName}
              </span>
            ) : (
              <span className="block text-sm font-normal text-gray-500 dark:text-gray-400 mt-1">
                {streamDisplayName}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="overflow-y-auto custom-scrollbar space-y-6 min-h-0">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              {/* ── Email Inboxes ── */}
              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-blue-500" /> Email Inboxes
                  </h3>
                  <Button type="button" variant="outline" size="sm" onClick={addInbox} className="text-xs">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Inbox
                  </Button>
                </div>

                {inboxes.length === 0 && (
                  <p className="text-xs text-gray-400 italic">No email inboxes configured for this stream.</p>
                )}

                {inboxes.map((inbox, i) => (
                  <div
                    key={i}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3 bg-gray-50 dark:bg-gray-800/50"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <Badge variant="outline" className="text-xs shrink-0">
                        Inbox {i + 1}
                      </Badge>
                      <button
                        type="button"
                        onClick={() => removeInbox(i)}
                        className="text-red-500 hover:text-red-700 shrink-0"
                        title="Remove inbox"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                          Mailbox (email address)
                        </label>
                        <Input
                          value={inbox.mailbox}
                          onChange={(e) => updateInbox(i, { mailbox: e.target.value })}
                          placeholder="support@example.com"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Type</label>
                        <select
                          value={inbox.type}
                          onChange={(e) => updateInbox(i, { type: e.target.value as 'shared' | 'user' })}
                          className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm"
                        >
                          <option value="shared">Shared mailbox</option>
                          <option value="user">User mailbox</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Label</label>
                        <Input
                          value={inbox.label || ''}
                          onChange={(e) => updateInbox(i, { label: e.target.value || undefined })}
                          placeholder="e.g. Support Inbox"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Folder</label>
                        <Input
                          value={inbox.folder || ''}
                          onChange={(e) => updateInbox(i, { folder: e.target.value || undefined })}
                          placeholder="Inbox"
                          className="text-sm"
                        />
                      </div>
                    </div>

                    <FilterEditor
                      filters={inbox.filters || []}
                      onChange={(filters) => updateInbox(i, { filters })}
                    />
                  </div>
                ))}
              </section>

              {/* ── Meeting Recordings ── */}
              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Video className="w-4 h-4 text-violet-500" /> Meeting Recordings
                  </h3>
                  {recording ? (
                    <Button type="button" variant="outline" size="sm" onClick={disableRecording} className="text-xs text-red-600 border-red-300">
                      Disable
                    </Button>
                  ) : (
                    <Button type="button" variant="outline" size="sm" onClick={enableRecording} className="text-xs">
                      Enable
                    </Button>
                  )}
                </div>

                {!recording ? (
                  <p className="text-xs text-gray-400 italic">Recording listener not enabled for this stream.</p>
                ) : (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3 bg-gray-50 dark:bg-gray-800/50">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Level</label>
                        <select
                          value={recording.level}
                          onChange={(e) => setRecording({ ...recording, level: e.target.value as 'tenant' | 'user' })}
                          className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm"
                        >
                          <option value="tenant">Tenant (all meetings)</option>
                          <option value="user">Specific user</option>
                        </select>
                      </div>
                      {recording.level === 'user' && (
                        <div>
                          <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                            User ID (Graph user ID or UPN)
                          </label>
                          <Input
                            value={recording.userId || ''}
                            onChange={(e) => setRecording({ ...recording, userId: e.target.value || undefined })}
                            placeholder="user@example.com"
                            className="text-sm"
                          />
                        </div>
                      )}
                      <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                          Transcript format
                        </label>
                        <select
                          value={recording.transcriptFormat || ''}
                          onChange={(e) => setRecording({ ...recording, transcriptFormat: e.target.value || undefined })}
                          className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm"
                        >
                          <option value="">None (recording only)</option>
                          <option value="vtt">VTT</option>
                          <option value="docx">DOCX</option>
                        </select>
                      </div>
                    </div>

                    <FilterEditor
                      filters={recording.filters || []}
                      onChange={(filters) => setRecording({ ...recording, filters })}
                    />

                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Use filters to match recordings by tags (e.g. <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">#sales</code> in subject),
                      organizer, attendees, or meeting subject keywords.
                    </p>
                  </div>
                )}
              </section>

              {/* ── Jira Service Desk ── */}
              <section className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Bug className="w-4 h-4 text-green-600" /> Jira Service Desk
                  </h3>
                  <Button type="button" variant="outline" size="sm" onClick={addJiraListener} className="text-xs">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Jira Listener
                  </Button>
                </div>

                {jiraListeners.length === 0 && (
                  <p className="text-xs text-gray-400 italic">No Jira listeners configured for this stream.</p>
                )}

                {jiraListeners.map((jl, i) => (
                  <div
                    key={i}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3 bg-gray-50 dark:bg-gray-800/50"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <Badge variant="outline" className="text-xs shrink-0">
                        Jira {i + 1}
                      </Badge>
                      <button
                        type="button"
                        onClick={() => removeJiraListener(i)}
                        className="text-red-500 hover:text-red-700 shrink-0"
                        title="Remove Jira listener"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                          Jira Base URL
                        </label>
                        <Input
                          value={jl.baseUrl}
                          onChange={(e) => updateJiraListener(i, { baseUrl: e.target.value })}
                          placeholder="https://myorg.atlassian.net"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                          Project Key
                        </label>
                        <Input
                          value={jl.projectKey}
                          onChange={(e) => updateJiraListener(i, { projectKey: e.target.value })}
                          placeholder="PROJ"
                          className="text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                          Label
                        </label>
                        <Input
                          value={jl.label || ''}
                          onChange={(e) => updateJiraListener(i, { label: e.target.value || undefined })}
                          placeholder="e.g. Customer Support Board"
                          className="text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-2">
                        Events to listen for
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {ALL_JIRA_EVENTS.map((evt) => {
                          const active = jl.events.includes(evt.value);
                          return (
                            <button
                              key={evt.value}
                              type="button"
                              onClick={() => toggleJiraEvent(i, evt.value)}
                              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                                active
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700'
                                  : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-green-300'
                              }`}
                            >
                              {evt.label}
                            </button>
                          );
                        })}
                      </div>
                      {jl.events.length === 0 && (
                        <p className="text-xs text-amber-500 mt-1">Select at least one event type.</p>
                      )}
                      <label className="mt-3 flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={jl.customerOnly === true}
                          onChange={(e) => updateJiraListener(i, { customerOnly: e.target.checked })}
                        />
                        Customer comments only (ignore staff comments and internal notes)
                      </label>
                      <label className="mt-3 block text-xs text-gray-700 dark:text-gray-300">
                        Engineer mention
                        <input
                          type="text"
                          value={jl.agentMention || ''}
                          onChange={(e) => updateJiraListener(i, { agentMention: e.target.value })}
                          placeholder="@maxiron_ai"
                          className="mt-1 w-full px-2 py-1.5 border rounded text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600"
                        />
                        <span className="mt-1 block text-gray-500 dark:text-gray-400">
                          A staff internal note containing this text wakes the agent. The reply is one internal note.
                        </span>
                      </label>
                    </div>

                    <FilterEditor
                      filters={jl.filters || []}
                      onChange={(filters) => updateJiraListener(i, { filters })}
                      fieldOptions={JIRA_FIELD_OPTIONS}
                      defaultField="summary"
                    />

                    <JiraWebhookStatus listener={jl} />

                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Filters apply to the incoming Jira event payload. Use fields like{' '}
                      <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">issueType</code>,{' '}
                      <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">priority</code>,{' '}
                      <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">labels</code>, or{' '}
                      <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">summary</code> to narrow events.
                    </p>
                  </div>
                ))}
              </section>
            </>
          )}

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="button" onClick={() => void handleSave()} disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
